<template>
  <!-- Placeholder pendant le chargement -->
  <div v-if="aspectRatio === null" :style="{ width: formatWidth(width) }" />
  
  <!-- Image unique -->
  <div
    v-else-if="!isSlider"
    :style="containerStyle"
    class="overflow-hidden"
  >
    <img
      :src="srcArray[0]"
      :alt="alt"
      :class="['w-full h-auto', className]"
      :style="imageStyle"
      @contextmenu.prevent
      @dragstart.prevent
    />
  </div>
  
  <!-- Slider d'images -->
  <div
    v-else
    ref="containerRef"
    :style="sliderContainerStyle"
    class="overflow-hidden"
  >
    <img
      v-for="(src, index) in srcArray"
      :key="`${src}-${index}`"
      :ref="(el) => setImageRef(el as HTMLImageElement | null, index)"
      :src="src"
      :alt="`${alt} ${index + 1}`"
      :class="['w-full h-auto', className]"
      :style="getImageSliderStyle(index)"
      @contextmenu.prevent
      @dragstart.prevent
    />
    
    <!-- Indicateurs de navigation -->
    <div
      v-if="srcArray.length > 1"
      :style="indicatorsStyle"
    >
      <button
        v-for="(_, index) in srcArray"
        :key="index"
        :style="getIndicatorStyle(index)"
        :aria-label="`Aller à l'image ${index + 1}`"
        @click="handleIndicatorClick(index)"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, watch, nextTick } from 'vue'
import { gsap } from 'gsap'

export interface ScreenSizeProps {
  mobile_s?: string | number
  mobile_m?: string | number
  mobile_l?: string | number
  tablet?: string | number
  laptop?: string | number
  laptop_l?: string | number
  desktop?: string | number
  k_screen?: string | number
}

export type AnimationType = 'slide' | 'fade' | 'zoom' | 'blur'

export interface ImageSliderOptions {
  interval?: number // en ms pour le slideshow, ex: 3000
  transitionDuration?: number // durée de transition en secondes
  autoplay?: boolean // lecture automatique ou non
  animation?: AnimationType // Type d'animation
}

export interface ImageProps {
  src: string | string[] // URL unique ou tableau d'URLs
  alt: string // Texte alternatif
  screen_size?: ScreenSizeProps
  className?: string // Classes CSS additionnelles
  containerStyle?: Record<string, any> // Styles en ligne pour le conteneur parent
  sliderOptions?: ImageSliderOptions // Options pour le slider
}

interface Props {
  src: string | string[]
  alt: string
  screen_size?: ScreenSizeProps
  className?: string
  containerStyle?: Record<string, any>
  sliderOptions?: ImageSliderOptions
}

const props = withDefaults(defineProps<Props>(), {
  className: '',
  containerStyle: undefined,
  screen_size: undefined,
  sliderOptions: undefined
})

const aspectRatio = ref<number | null>(null)
const width = ref<string | number>(props.screen_size?.mobile_s || '100%')
const currentIndex = ref(0)

const containerRef = ref<HTMLDivElement | null>(null)
const imageRefs = ref<(HTMLImageElement | null)[]>([])
const intervalRef = ref<ReturnType<typeof setInterval> | null>(null)
const timelineRef = ref<gsap.core.Timeline | null>(null)

// Convertir src en tableau pour uniformiser le traitement
const srcArray = computed(() => Array.isArray(props.src) ? props.src : [props.src])
const isSlider = computed(() => srcArray.value.length > 1)

// Options par défaut pour le slider
const sliderOptions = computed(() => ({
  interval: 5000,
  transitionDuration: 0.8,
  autoplay: true,
  animation: 'slide' as AnimationType,
  ...props.sliderOptions
}))

// Fonction utilitaire pour convertir un nombre en chaîne avec "px"
const formatWidth = (value: string | number): string =>
  typeof value === 'number' ? `${value}px` : value

// Chargement des dimensions de l'image pour calculer le ratio
const loadAspectRatio = () => {
  const img = new window.Image()
  img.src = srcArray.value[0]
  img.onload = () => {
    const ratio = img.naturalWidth / img.naturalHeight
    aspectRatio.value = ratio
  }
}

// Mise à jour de la largeur en fonction de la taille de l'écran
const updateWidth = () => {
  if (!props.screen_size) {
    width.value = '100%'
    return
  }

  let newWidth: string | number = '100%'

  if (window.innerWidth <= 320) {
    newWidth = props.screen_size.mobile_s ?? '100%'
  } else if (window.innerWidth <= 375) {
    newWidth = props.screen_size.mobile_m ?? '100%'
  } else if (window.innerWidth <= 425) {
    newWidth = props.screen_size.mobile_l ?? '100%'
  } else if (window.innerWidth <= 768) {
    newWidth = props.screen_size.tablet ?? '100%'
  } else if (window.innerWidth <= 1024) {
    newWidth = props.screen_size.laptop ?? '100%'
  } else if (window.innerWidth <= 1440) {
    newWidth = props.screen_size.laptop_l ?? '100%'
  } else if (window.innerWidth <= 1920) {
    newWidth = props.screen_size.desktop ?? '100%'
  } else if (window.innerWidth <= 2560) {
    newWidth = props.screen_size.k_screen ?? '100%'
  }

  width.value = formatWidth(newWidth)
}

// Animation de transition entre les images
const animateToNext = (nextIndex: number) => {
  const currentImg = imageRefs.value[currentIndex.value]
  const nextImg = imageRefs.value[nextIndex]

  if (!currentImg || !nextImg || !containerRef.value) return

  // Nettoyer l'ancienne timeline
  if (timelineRef.value) {
    timelineRef.value.kill()
  }

  // Créer une nouvelle timeline
  timelineRef.value = gsap.timeline({
    onComplete: () => {
      currentIndex.value = nextIndex
    }
  })

  const duration = sliderOptions.value.transitionDuration

  switch (sliderOptions.value.animation) {
    case 'fade':
      // Animation fade: crossfade entre les images
      gsap.set(nextImg, {
        opacity: 0,
        zIndex: 2,
        visibility: 'visible',
        x: '0%',
        scale: 1,
        filter: 'blur(0px)'
      })

      gsap.set(currentImg, {
        opacity: 1,
        zIndex: 1,
        visibility: 'visible',
        x: '0%'
      })

      timelineRef.value
        .to(currentImg, {
          opacity: 0,
          duration: duration,
          ease: 'power2.inOut'
        })
        .to(nextImg, {
          opacity: 1,
          duration: duration,
          ease: 'power2.inOut'
        }, 0)
        .set(currentImg, {
          opacity: 0,
          zIndex: 0,
          visibility: 'hidden'
        })
        .set(nextImg, {
          opacity: 1,
          zIndex: 1,
          visibility: 'visible'
        })
      break

    case 'zoom':
      // Animation zoom: zoom in de la nouvelle image
      gsap.set(nextImg, {
        opacity: 0,
        scale: 1.2,
        zIndex: 2,
        visibility: 'visible',
        x: '0%',
        filter: 'blur(0px)'
      })

      gsap.set(currentImg, {
        opacity: 1,
        scale: 1,
        zIndex: 1,
        visibility: 'visible',
        x: '0%'
      })

      timelineRef.value
        .to(currentImg, {
          opacity: 0,
          scale: 0.9,
          duration: duration,
          ease: 'power2.inOut'
        })
        .to(nextImg, {
          opacity: 1,
          scale: 1,
          duration: duration,
          ease: 'power2.inOut'
        }, 0)
        .set(currentImg, {
          opacity: 0,
          scale: 1,
          zIndex: 0,
          visibility: 'hidden'
        })
        .set(nextImg, {
          opacity: 1,
          scale: 1,
          zIndex: 1,
          visibility: 'visible'
        })
      break

    case 'blur':
      // Animation blur: transition avec effet de flou
      gsap.set(nextImg, {
        opacity: 0,
        filter: 'blur(20px)',
        zIndex: 2,
        visibility: 'visible',
        x: '0%',
        scale: 1
      })

      gsap.set(currentImg, {
        opacity: 1,
        filter: 'blur(0px)',
        zIndex: 1,
        visibility: 'visible',
        x: '0%'
      })

      timelineRef.value
        .to(currentImg, {
          opacity: 0,
          filter: 'blur(20px)',
          duration: duration,
          ease: 'power2.inOut'
        })
        .to(nextImg, {
          opacity: 1,
          filter: 'blur(0px)',
          duration: duration,
          ease: 'power2.inOut'
        }, 0)
        .set(currentImg, {
          opacity: 0,
          filter: 'blur(0px)',
          zIndex: 0,
          visibility: 'hidden'
        })
        .set(nextImg, {
          opacity: 1,
          filter: 'blur(0px)',
          zIndex: 1,
          visibility: 'visible'
        })
      break

    case 'slide':
    default:
      // Animation slide: glissement horizontal
      gsap.set(nextImg, {
        x: '100%',
        opacity: 1,
        zIndex: 2,
        visibility: 'visible',
        scale: 1,
        filter: 'blur(0px)'
      })

      gsap.set(currentImg, {
        x: '0%',
        opacity: 1,
        zIndex: 1,
        visibility: 'visible'
      })

      timelineRef.value
        .to(currentImg, {
          x: '-100%',
          duration: duration,
          ease: 'power2.inOut'
        })
        .to(nextImg, {
          x: '0%',
          duration: duration,
          ease: 'power2.inOut'
        }, 0)
        .set(currentImg, {
          x: '0%',
          opacity: 0,
          zIndex: 0,
          visibility: 'hidden'
        })
        .set(nextImg, {
          x: '0%',
          opacity: 1,
          zIndex: 1,
          visibility: 'visible'
        })
      break
  }
}

// Gestion de l'autoplay
const startAutoplay = () => {
  if (!isSlider.value || !sliderOptions.value.autoplay) return

  if (intervalRef.value) {
    clearInterval(intervalRef.value)
  }

  intervalRef.value = setInterval(() => {
    const nextIdx = (currentIndex.value + 1) % srcArray.value.length
    animateToNext(nextIdx)
  }, sliderOptions.value.interval)
}

const stopAutoplay = () => {
  if (intervalRef.value) {
    clearInterval(intervalRef.value)
    intervalRef.value = null
  }
}

// Gestion du clic sur les indicateurs
const handleIndicatorClick = (index: number) => {
  if (index !== currentIndex.value) {
    stopAutoplay()
    animateToNext(index)
    startAutoplay()
  }
}

// Set image ref
const setImageRef = (el: HTMLImageElement | null, index: number) => {
  if (el) {
    imageRefs.value[index] = el
  }
}

// Computed styles
const containerStyle = computed(() => {
  if (aspectRatio.value === null) return {}
  
  const baseStyle: Record<string, any> = {
    width: formatWidth(width.value),
    height: `calc(${formatWidth(width.value)} / ${aspectRatio.value})`,
    position: 'relative',
    overflow: 'hidden',
    ...props.containerStyle
  }
  
  return baseStyle
})

const imageStyle = computed(() => ({
  width: '100%',
  height: '100%',
  objectFit: 'contain' as const
}))

const sliderContainerStyle = computed(() => {
  if (aspectRatio.value === null) return {}
  
  return {
    width: formatWidth(width.value),
    height: `calc(${formatWidth(width.value)} / ${aspectRatio.value})`,
    position: 'relative',
    overflow: 'hidden',
    ...props.containerStyle
  }
})

const getImageSliderStyle = (index: number) => ({
  width: '100%',
  height: '100%',
  objectFit: 'contain' as const,
  position: 'absolute' as const,
  top: 0,
  left: 0,
  opacity: index === currentIndex.value ? 1 : 0,
  visibility: (index === currentIndex.value ? 'visible' : 'hidden') as 'visible' | 'hidden',
  zIndex: index === currentIndex.value ? 1 : 0
})

const indicatorsStyle = computed(() => ({
  position: 'absolute' as const,
  bottom: '10px',
  left: '50%',
  transform: 'translateX(-50%)',
  display: 'flex' as const,
  gap: '8px',
  zIndex: 10
}))

const getIndicatorStyle = (index: number) => ({
  width: '8px',
  height: '8px',
  borderRadius: '50%',
  border: 'none',
  backgroundColor: index === currentIndex.value ? '#ffffff' : 'rgba(255, 255, 255, 0.5)',
  cursor: 'pointer',
  transition: 'background-color 0.3s ease'
})

// Lifecycle hooks
onMounted(() => {
  updateWidth()
  window.addEventListener('resize', updateWidth)
  loadAspectRatio()
})

onUnmounted(() => {
  window.removeEventListener('resize', updateWidth)
  stopAutoplay()
  if (timelineRef.value) {
    timelineRef.value.kill()
  }
})

// Watch pour l'autoplay
watch([currentIndex, isSlider, () => sliderOptions.value.autoplay, () => sliderOptions.value.interval, () => srcArray.value.length], () => {
  stopAutoplay()
  if (isSlider.value && sliderOptions.value.autoplay) {
    nextTick(() => {
      startAutoplay()
    })
  }
}, { immediate: true })

// Watch pour le changement de src
watch(() => props.src, () => {
  currentIndex.value = 0
  imageRefs.value = []
  loadAspectRatio()
})

// Watch pour screen_size
watch(() => props.screen_size, () => {
  updateWidth()
}, { deep: true })
</script>

<style scoped>
/* Styles additionnels si nécessaire */
</style>
